import { type FC, useMemo, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { datadogRum } from '@datadog/browser-rum';
import { Badge } from 'dibs-elements/exports/Badge.tsx';
import Dot from 'dibs-icons/exports/Dot.ts';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import { toDataTn } from '../NavItems/helpers.ts';
import { useNavContext } from '../../NavContext.tsx';
import { useNotificationsConfig } from '../../helpers/useNotificationsConfig.ts';
import { userServerVars } from '../../userServerVars.ts';
import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    interactionTypeConstants,
} from 'dibs-tracking';

import styles from './styles/NavItemBadge.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type NavItemBadge_seller$key } from './__generated__/NavItemBadge_seller.graphql.ts';
import { type NavItemBadge_notifications$key } from './__generated__/NavItemBadge_notifications.graphql.ts';
import { type NavItemBadge_viewer$key } from './__generated__/NavItemBadge_viewer.graphql.ts';

type Props = {
    dataTnKey?: string;
    type: string | null;
    seller?: NavItemBadge_seller$key | null;
    notifications?: NavItemBadge_notifications$key | null;
    viewer?: NavItemBadge_viewer$key | null;
};

const sellerFragment = graphql`
    fragment NavItemBadge_seller on Seller {
        ...useNotificationsConfig_seller
    }
`;

const notificationsFragment = graphql`
    fragment NavItemBadge_notifications on DealerNavBarBadges {
        ...useNotificationsConfig_notifications
    }
`;

const viewerFragment = graphql`
    fragment NavItemBadge_viewer on Viewer {
        ...useNotificationsConfig_viewer
    }
`;

export const NavItemBadge: FC<Props> = ({
    dataTnKey,
    type,
    seller: sellerRef,
    notifications: notificationsRef,
    viewer: viewerRef,
}) => {
    const containerRef = useRef(null);
    const seller = useFragment(sellerFragment, sellerRef);
    const notifications = useFragment(notificationsFragment, notificationsRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { status } = useNavContext();
    const notificationsConfig = useNotificationsConfig({ seller, notifications, viewer });
    const { isDealer, loginDealer } = userServerVars;

    const isDealerLayout = isDealer || loginDealer;

    // Check if this nav item should show an alert dot
    const shouldShowActionableItemAlert =
        notificationsConfig[dataTnKey as keyof typeof notificationsConfig]?.isActionable;

    const config = useMemo(
        () =>
            hasKey(notificationsConfig, type)
                ? notificationsConfig[type]
                : { count: '0', dataTn: '', showCount: false },
        [notificationsConfig, type]
    );
    const dataTn = `navbar-badge-${toDataTn(config?.dataTn)}`;

    const [count, isCeil] = config?.count || ['0', false];
    const showBadge = Number(count) > 0 || isCeil || shouldShowActionableItemAlert;
    const showCount = config?.showCount && !shouldShowActionableItemAlert;

    if (!showBadge || !isDealerLayout) {
        return null;
    }

    return (
        <div ref={containerRef}>
            <VisibilityTracker
                elementRef={containerRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackEvent({
                            eventName: eventNameConstants.EVENT_NAVIGATION,
                            step_interaction_name:
                                stepInteractionConstants.STEP_INTERACTION_IMPRESSION,
                            interaction_type: interactionTypeConstants.INTERACTION_TYPE_RED_DOT,
                            trigger: dataTn,
                        });
                        datadogRum.addAction(`${dataTn}-Badge-Displayed`);
                        datadogRum.addAction(`${type}-Notification-Displayed`, {
                            navBarNotificationDisplayed: showBadge,
                            navBarNotificationConfig: config,
                        });
                    }
                }}
            />
            {status === 'expanded' || status === 'overlay' ? (
                <div
                    data-tn={dataTn}
                    className={classnames(dibsCss.mlXsmall, {
                        [styles.badge]: !!showCount,
                        [styles.dot]: !showCount,
                        [dibsCss.fillAlertprimary]: !showCount,
                    })}
                >
                    {showCount ? <Badge type="alert">{count}</Badge> : <Dot variation="20" />}
                </div>
            ) : (
                <div className={classnames(dibsCss.fillAlertprimary, styles.smallDot)}>
                    <Dot variation="16" />
                </div>
            )}
        </div>
    );
};
